import * as THREE from "three";
import type { EnvironmentPreset } from "./environmentPresets";
import { DEFAULT_ENVIRONMENT } from "./environmentPresets";
import { observationTuning } from "./observationTuning";

export interface ObservationLighting {
  setPreset: (preset: EnvironmentPreset) => void;
  update: (deltaSeconds: number) => void;
}

export function addObservationLighting(scene: THREE.Scene, worldVector: THREE.Vector3): ObservationLighting {
  const initial = DEFAULT_ENVIRONMENT.lighting;
  let target = initial;
  const targetHemisphereSky = new THREE.Color(initial.hemisphereSky);
  const targetHemisphereGround = new THREE.Color(initial.hemisphereGround);
  const targetKey = new THREE.Color(initial.keyColor);
  const targetDorsalFill = new THREE.Color(initial.dorsalFillColor);
  const targetRim = new THREE.Color(initial.rimColor);
  const targetInterior = new THREE.Color(initial.interiorColor);
  const motionSettings = observationTuning.lightingMotion;

  const hemisphere = new THREE.HemisphereLight(
    initial.hemisphereSky,
    initial.hemisphereGround,
    initial.hemisphereIntensity,
  );
  scene.add(hemisphere);

  const key = new THREE.DirectionalLight(initial.keyColor, initial.keyIntensity);
  key.position.copy(worldVector).multiplyScalar(6).add(new THREE.Vector3(1.5, 3.5, 2));
  const keyBasePosition = key.position.clone();
  scene.add(key);

  // A broad, shadow-free light above and slightly in front of the specimen
  // keeps dorsal shell patterns legible without flattening the main lighting.
  const dorsalFill = new THREE.DirectionalLight(initial.dorsalFillColor, initial.dorsalFillIntensity);
  dorsalFill.position.set(0.4, 7, 3.5);
  const dorsalFillBasePosition = dorsalFill.position.clone();
  scene.add(dorsalFill);

  const rim = new THREE.DirectionalLight(initial.rimColor, initial.rimIntensity);
  rim.position.copy(worldVector).multiplyScalar(-5).add(new THREE.Vector3(-1, 1.5, -2));
  const rimBasePosition = rim.position.clone();
  scene.add(rim);

  const interior = new THREE.PointLight(initial.interiorColor, initial.interiorIntensity, 5, 1.5);
  interior.position.set(0, -0.18, 0.25);
  scene.add(interior);

  let elapsedSeconds = 0;
  let hemisphereBaseIntensity = initial.hemisphereIntensity;
  let keyBaseIntensity = initial.keyIntensity;
  let dorsalFillBaseIntensity = initial.dorsalFillIntensity;
  let rimBaseIntensity = initial.rimIntensity;
  let interiorBaseIntensity = initial.interiorIntensity;
  const verticalAxis = new THREE.Vector3(0, 1, 0);
  const lateralAxis = new THREE.Vector3(1, 0, 0);
  return {
    setPreset(preset): void {
      target = preset.lighting;
      targetHemisphereSky.setHex(target.hemisphereSky);
      targetHemisphereGround.setHex(target.hemisphereGround);
      targetKey.setHex(target.keyColor);
      targetDorsalFill.setHex(target.dorsalFillColor);
      targetRim.setHex(target.rimColor);
      targetInterior.setHex(target.interiorColor);
    },
    update(deltaSeconds): void {
      const easing = 1 - Math.exp(-deltaSeconds * 1.8);
      elapsedSeconds += deltaSeconds;
      const phase = elapsedSeconds / motionSettings.cycleSeconds * Math.PI * 2;
      const depth = motionSettings.intensityDepth;
      const drift = THREE.MathUtils.degToRad(motionSettings.driftDegrees);

      hemisphere.color.lerp(targetHemisphereSky, easing);
      hemisphere.groundColor.lerp(targetHemisphereGround, easing);
      hemisphereBaseIntensity = damp(hemisphereBaseIntensity, target.hemisphereIntensity, easing);
      key.color.lerp(targetKey, easing);
      keyBaseIntensity = damp(keyBaseIntensity, target.keyIntensity, easing);
      dorsalFill.color.lerp(targetDorsalFill, easing);
      dorsalFillBaseIntensity = damp(dorsalFillBaseIntensity, target.dorsalFillIntensity, easing);
      rim.color.lerp(targetRim, easing);
      rimBaseIntensity = damp(rimBaseIntensity, target.rimIntensity, easing);
      interior.color.lerp(targetInterior, easing);
      interiorBaseIntensity = damp(interiorBaseIntensity, target.interiorIntensity, easing);

      hemisphere.intensity = hemisphereBaseIntensity * (1 + Math.sin(phase * 0.47 + 0.8) * depth * 0.35);
      key.intensity = keyBaseIntensity * (1 + Math.sin(phase) * depth);
      dorsalFill.intensity = dorsalFillBaseIntensity * (1 + Math.sin(phase * 0.39 + 1.4) * depth * 0.2);
      rim.intensity = rimBaseIntensity * (1 + Math.sin(phase * 0.83 + 2.1) * depth * 0.8);
      interior.intensity = interiorBaseIntensity * (1 + Math.sin(phase * 0.61 + 4.2) * depth * 0.5);

      key.position.copy(keyBasePosition)
        .applyAxisAngle(verticalAxis, Math.sin(phase * 0.73) * drift)
        .applyAxisAngle(lateralAxis, Math.sin(phase * 0.41 + 1.1) * drift * 0.35);
      dorsalFill.position.copy(dorsalFillBasePosition)
        .applyAxisAngle(verticalAxis, Math.sin(phase * 0.31 + 0.5) * drift * 0.25);
      rim.position.copy(rimBasePosition)
        .applyAxisAngle(verticalAxis, Math.sin(phase * 0.57 + 2.4) * drift * 0.85)
        .applyAxisAngle(lateralAxis, Math.sin(phase * 0.37 + 3.2) * drift * 0.28);
    },
  };
}

function damp(current: number, target: number, easing: number): number {
  return THREE.MathUtils.lerp(current, target, easing);
}
